// /client/src/components/PodDeploymentView.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navigation from './Navigation';

const PodDeploymentView = () => {
  const navigate = useNavigate();
  const { id: deploymentId, podId } = useParams();
  const chatEndRef = useRef(null);
  
  // Ref to track if a send is in progress (survives re-renders)
  const sendInProgressRef = useRef(false);

  const [deployment, setDeployment] = useState(null);
  const [pod, setPod] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Messaging enabled state
  const [canSendMessages, setCanSendMessages] = useState(true);
  const [disabledReason, setDisabledReason] = useState('');

  // Chat input state
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [attachment, setAttachment] = useState(null); // { type: 'media'|'link', url: '', file: null }
  const [uploading, setUploading] = useState(false);

  // Detect if the message input contains a link (used to toggle media button)
  const messageHasLink = /https?:\/\/[^\s]+/.test(newMessage);

  // Upload media to server
  const uploadMedia = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'messages');

    const token = localStorage.getItem('token');
    const response = await fetch('/api/media/upload', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Upload failed');
    }

    return (await response.json()).media;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'video/mp4'];
    if (!validTypes.includes(file.type)) {
      setSendError('Only JPG, PNG, GIF, or MP4 files allowed');
      e.target.value = '';
      return;
    }

    const maxSize = file.type.startsWith('video') ? 1048576 : 512000;
    if (file.size > maxSize) {
      setSendError(`File too large. Max ${file.type.startsWith('video') ? '1MB' : '500KB'}`);
      e.target.value = '';
      return;
    }

    try {
      setUploading(true);
      setSendError('');
      const media = await uploadMedia(file);
      setAttachment({ type: 'media', url: media.url, filename: media.filename, mimeType: media.mimeType });
    } catch (err) {
      setSendError(err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleAddLink = () => {
    const url = prompt('Enter URL:');
    if (url && url.match(/^https?:\/\/.+/)) {
      setNewMessage(prev => prev ? `${prev} ${url}` : url);
    } else if (url) {
      setSendError('Invalid URL. Must start with http:// or https://');
    }
  };

  const removeAttachment = () => setAttachment(null);

  // Initial data fetch
  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/deployments/${deploymentId}/pods/${podId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch pod data');

      const data = await response.json();
      setDeployment(data.deployment);
      setPod(data.pod);
      
      // Check if messaging is allowed
      // Consider both deployment-level and pod-level status
      const isDeploymentTerminal = data.deployment?.status === 'completed' || data.deployment?.status === 'cancelled';
      const isPodTerminal = data.pod?.deploymentStatus === 'completed' || data.pod?.deploymentStatus === 'cancelled';
      const isRelayActive = data.pod?.activeConversation?.isActive === true;
      
      if (isDeploymentTerminal) {
        setCanSendMessages(false);
        setDisabledReason('This deployment is complete. No further messages can be sent.');
      } else if (isPodTerminal) {
        setCanSendMessages(false);
        setDisabledReason(`This pod has been ${data.pod.deploymentStatus}. No further messages can be sent.`);
      } else if (!isRelayActive) {
        setCanSendMessages(false);
        setDisabledReason('Pod relay is not active. Messages cannot be sent until deployment begins.');
      } else {
        setCanSendMessages(true);
        setDisabledReason('');
      }
      
      // Process messages: dedupe outbound, show all statuses including pending
      const processedMessages = processMessages(data.messages || []);
      setMessages(processedMessages);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [deploymentId, podId]);

  // Process messages: dedupe outbound by messageLabel/timestamp
  const processMessages = (rawMessages) => {
    const result = [];
    const seenOutbound = new Set();

    for (const msg of rawMessages) {
      if (msg.direction === 'outbound') {
        // Dedupe by messageLabel + approximate timestamp (within same minute)
        const timestamp = new Date(msg.timestamp);
        const timeKey = `${timestamp.getFullYear()}-${timestamp.getMonth()}-${timestamp.getDate()}-${timestamp.getHours()}-${timestamp.getMinutes()}`;
        const dedupeKey = `${msg.messageLabel || 'msg'}-${timeKey}`;
        
        if (seenOutbound.has(dedupeKey)) continue;
        seenOutbound.add(dedupeKey);
      }
      
      result.push(msg);
    }

    // Sort by timestamp
    result.sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return timeA - timeB;
    });
    
    return result;
  };

  // Manual refresh handler
  const handleRefresh = () => {
    fetchData(true);
  };

  // Send message handler - with duplicate prevention
  // UPDATED: Always require text in the input to send
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    // Always require text content to send
    if (!newMessage.trim() || sending || sendInProgressRef.current) {
      return;
    }

    sendInProgressRef.current = true;
    
    try {
      setSending(true);
      setSendError('');
      const token = localStorage.getItem('token');

      const payload = { message: newMessage.trim() };
      if (attachment && attachment.type === 'media') {
        payload.mediaUrl = attachment.url;
      }
      
      const response = await fetch(`/api/deployments/${deploymentId}/pods/${podId}/send`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error(data.error || 'Please wait before sending another message');
        }
        if (response.status === 403) {
          // Deployment ended or relay inactive
          setCanSendMessages(false);
          setDisabledReason(data.error || 'Messaging is no longer available for this deployment.');
          throw new Error(data.error || 'Messaging is no longer available');
        }
        throw new Error(data.error || 'Failed to send message');
      }

      setNewMessage('');
      setAttachment(null);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchData(true);
    } catch (err) {
      setSendError(err.message);
    } finally {
      setSending(false);
      setTimeout(() => {
        sendInProgressRef.current = false;
      }, 1000);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatPhone = (phone) => {
    if (!phone) return 'N/A';
    const digits = phone.replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) return phone;
    return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get bubble color based on message direction and status
  const getBubbleStyle = (msg) => {
    if (msg.direction === 'inbound') {
      return {
        backgroundColor: '#a9a9a9',
        color: '#FFFFFF'
      };
    }
    
    if (msg.status === 'failed' || msg.status === 'undelivered') {
      return {
        backgroundColor: '#ffcccc',
        color: '#333333',
        border: '1px solid #ff6666'
      };
    }
    
    if (msg.status === 'queued' || msg.status === 'scheduled' || msg.status === 'sending' || msg.status === 'pending') {
      return {
        backgroundColor: '#b8d4e8',
        color: '#333333'
      };
    }
    
    return {
      backgroundColor: '#87bde7',
      color: '#FFFFFF'
    };
  };

  // ============================================================
  // FIX: Enhanced media URL extraction
  // ============================================================
  const extractMediaUrls = (msg) => {
    const allMediaUrls = new Set();
    
    const addUrl = (url) => {
      if (!url || typeof url !== 'string') return;
      url = url.trim();
      if (!url.startsWith('http')) return;
      if (url.includes('api.twilio.com') && url.includes('/Media/')) return;
      allMediaUrls.add(url);
    };
    
    const processArrayOrString = (value) => {
      if (!value) return;
      if (Array.isArray(value)) { value.forEach(addUrl); return; }
      if (typeof value === 'string') {
        value = value.trim();
        if (value.startsWith('http')) { addUrl(value); return; }
        if (value.startsWith('[') || value.startsWith('{')) {
          try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) parsed.forEach(addUrl);
            else if (typeof parsed === 'object' && parsed !== null && parsed.url) addUrl(parsed.url);
          } catch { /* ignore */ }
        }
      }
    };
    
    addUrl(msg.mediaUrl);
    processArrayOrString(msg.mediaUrls);
    processArrayOrString(msg.parsedMediaUrls);
    if (msg.metadata) {
      processArrayOrString(msg.metadata.mediaUrls);
      processArrayOrString(msg.metadata.mediaUrl);
      processArrayOrString(msg.metadata.publicMediaUrls);
    }
    if (msg.queueData) {
      processArrayOrString(msg.queueData.mediaUrls);
      addUrl(msg.queueData.mediaUrl);
    }
    if (msg.productMedia) addUrl(msg.productMedia);
    
    return Array.from(allMediaUrls);
  };

  const isVideoUrl = (url) => {
    if (!url) return false;
    const lowerUrl = url.toLowerCase();
    return lowerUrl.match(/\.(mp4|mov|3gp|webm|avi|mkv)($|\?)/i) !== null ||
           lowerUrl.includes('video/') ||
           lowerUrl.includes('/video');
  };

  const MediaDisplay = ({ url, index }) => {
    const [loadError, setLoadError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    
    if (loadError) {
      return (
        <div className="mt-2">
          <a href={url} target="_blank" rel="noopener noreferrer" className="is-size-7" style={{ color: '#0954AF' }}>
            📎 View attachment {index > 0 ? `(${index + 1})` : ''}
          </a>
        </div>
      );
    }
    
    if (isVideoUrl(url)) {
      return (
        <div className={index > 0 ? 'mt-2' : ''}>
          <video 
            controls 
            style={{ maxWidth: '250px', maxHeight: '200px', borderRadius: '8px', backgroundColor: '#f0f0f0' }}
            onError={() => setLoadError(true)}
            onLoadedData={() => setIsLoading(false)}
          >
            <source src={url} />
            Your browser does not support video.
          </video>
        </div>
      );
    }
    
    return (
      <div className={index > 0 ? 'mt-2' : ''} style={{ position: 'relative' }}>
        {isLoading && (
          <div style={{
            position: 'absolute', top: 0, left: 0, width: '100px', height: '100px',
            backgroundColor: '#f0f0f0', borderRadius: '8px',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <span className="is-size-7 has-text-grey">Loading...</span>
          </div>
        )}
        <a href={url} target="_blank" rel="noopener noreferrer">
          <img 
            src={url} alt={`Attachment ${index + 1}`}
            style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', cursor: 'pointer', display: isLoading ? 'none' : 'block' }}
            onLoad={() => setIsLoading(false)}
            onError={() => { setIsLoading(false); setLoadError(true); }}
          />
        </a>
      </div>
    );
  };

  // Helper to get deployment status tag
  const getDeploymentStatusTag = () => {
    const deplStatus = deployment?.status;
    if (deplStatus === 'completed') return { label: 'Completed', className: 'is-success' };
    if (deplStatus === 'cancelled') return { label: 'Cancelled', className: 'is-danger' };
    if (deplStatus === 'in delivery') return { label: 'In Delivery', className: 'is-info' };
    if (deplStatus === 'scheduled') return { label: 'Scheduled', className: 'is-info' };
    if (deplStatus === 'draft') return { label: 'Draft', className: 'is-light' };
    return null;
  };

  // Helper to get pod-level status tag (only shown when different from deployment)
  const getPodStatusTag = () => {
    const podStatus = pod?.deploymentStatus;
    if (podStatus === 'cancelled') return { label: 'Pod Cancelled', className: 'is-danger' };
    if (podStatus === 'completed') return { label: 'Pod Completed', className: 'is-success' };
    if (podStatus === 'active') return { label: 'Pod Active', className: 'is-warning' };
    return null;
  };

  if (loading) {
    return (
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          <Navigation />
          <div className='mt-6 pt-6 container'>
            <p className="has-text-centered py-6">Loading...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error || !pod) {
    return (
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          <Navigation />
          <div className='mt-6 pt-6 container px-4'>
            <div className="notification is-danger">{error || 'Pod not found'}</div>
            <button className="button" onClick={() => navigate(`/deployments/${deploymentId}`)}>
              ← Back
            </button>
          </div>
        </div>
      </section>
    );
  }

  // ============================================================
  // COMPUTED: Determine which buttons are disabled and what helper text to show
  // ============================================================
  const hasMediaAttachment = attachment && attachment.type === 'media';
  const hasTextContent = newMessage.trim().length > 0;

  // Send is disabled unless there is text in the input
  const isSendDisabled = !hasTextContent || sending || uploading;

  // Media button disabled when message contains a link, or already sending/uploading
  const isMediaDisabled = messageHasLink || uploading || sending;

  // Link button disabled when a media attachment is present, or already sending/uploading
  const isLinkDisabled = hasMediaAttachment || uploading || sending;

  // Helper text logic
  let helperText = '';
  if (hasMediaAttachment && !hasTextContent) {
    helperText = 'A message is required to send a media item.';
  } else if (messageHasLink && !hasTextContent) {
    helperText = 'A message is required to send a link item.';
  }

  const deploymentStatusTag = getDeploymentStatusTag();
  const podStatusTag = getPodStatusTag();

  return (
    <section className="theme-light m-0 p-0" data-theme="light">
      <div className='is-flex is-flex-direction-column is-justify-content-center'>
        <Navigation />

        <div className='mt-2 pt-6 container px-6'>
          {/* Header */}
          <div className="mb-4">
            <button 
              className="button is-small is-text mb-2"
              onClick={() => navigate(`/deployments/${deploymentId}`)}
            >
              ← Back
            </button>
            <h1 className='h1 my-2'>{pod.podName}</h1>
            <p className="has-text-grey">
              {deployment?.name} • {pod.members?.length || 0} members
              {deploymentStatusTag && (
                <span className={`tag ml-2 ${deploymentStatusTag.className}`}>
                  {deploymentStatusTag.label}
                </span>
              )}
              {podStatusTag && (
                <span className={`tag ml-1 ${podStatusTag.className}`}>
                  {podStatusTag.label}
                </span>
              )}
            </p>
          </div>

          {/* Two Column Layout */}
          <div className="columns">
            {/* Left Column - Pod Members */}
            <div className="column is-one-third">
              <div className="box" style={{ height: '600px', overflowY: 'auto' }}>
                <h2 className="subtitle is-5 mb-4 is-bold">Members</h2>
                
                {pod.members?.map((member, idx) => (
                  <div 
                    key={member._id || idx}
                    className="mb-3 p-3"
                    style={{ backgroundColor: '#f5f5f5', borderRadius: '6px' }}
                  >
                    <div className="is-flex is-justify-content-space-between is-align-items-center">
                      <div>
                        <p className="has-text-weight-bold">
                          {member.firstName} {member.lastName}
                        </p>
                        <p className="is-size-7 has-text-grey">
                          {formatPhone(member.phone)}
                        </p>
                      </div>
                      {member.smsVerified ? (
                        <span className="tag is-success is-small">MMS</span>
                      ) : (
                        <span className="tag is-warning is-small">No MMS</span>
                      )}
                    </div>
                  </div>
                ))}

                {(!pod.members || pod.members.length === 0) && (
                  <p className="has-text-grey is-size-7">No members in this pod</p>
                )}
              </div>
            </div>

            {/* Right Column - Chat Interface */}
            <div className="column is-two-thirds">
              <div className="box" style={{ height: '600px', display: 'flex', flexDirection: 'column' }}>
                <div className="is-flex is-justify-content-space-between is-align-items-center mb-3">
                  <h2 className="subtitle is-5 mb-0 is-bold">Messages</h2>
                  <button 
                    className={`button is-small is-light ${refreshing ? 'is-loading' : ''}`}
                    onClick={handleRefresh}
                    disabled={refreshing}
                  >
                    {refreshing ? 'Loading...' : 'Reload'}
                  </button>
                </div>

                {/* Messages Area */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
                  {messages.length === 0 ? (
                    <p className="has-text-centered colorPureBlack is-size-7 mt-6">
                      No messages sent yet
                    </p>
                  ) : (
                    messages.map((msg, idx) => {
                      const bubbleStyle = getBubbleStyle(msg);
                      const mediaUrls = extractMediaUrls(msg);
                      
                      return (
                        <div 
                          key={msg._id || idx}
                          className={`mb-3 is-flex ${msg.direction === 'outbound' ? 'is-justify-content-flex-end' : 'is-justify-content-flex-start'}`}
                        >
                          <div 
                            style={{
                              maxWidth: '70%',
                              padding: '10px 14px',
                              borderRadius: msg.direction === 'outbound' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                              ...bubbleStyle
                            }}
                          >
                            {/* Sender info for inbound */}
                            {msg.direction === 'inbound' && (
                              <div className="is-size-7 mb-1 colorPureBlack">
                                <strong>{msg.senderName || 'Unknown'}</strong>
                                {msg.from && <span className="ml-2 colorPureBlack">{formatPhone(msg.from)}</span>}
                              </div>
                            )}

                            {/* Message label for outbound */}
                            {msg.direction === 'outbound' && (
                              <div className="is-size-7 mb-1" style={{ color: (msg.status === 'failed' || msg.status === 'undelivered') ? '#cc0000' : 'inherit' }}>
                                <strong>{msg.messageLabel || 'Campaign Message'}</strong>
                                {msg.status === 'failed' && (
                                  <span className="ml-2" style={{ color: '#cc0000' }}>Failed</span>
                                )}
                                {msg.status === 'undelivered' && (
                                  <span className="ml-2" style={{ color: '#cc0000' }}>Undelivered</span>
                                )}
                                {msg.status === 'cancelled' && (
                                  <span className="ml-2" style={{ color: '#cc0000' }}>Cancelled</span>
                                )}
                                {msg.status === 'pending' && (
                                  <span className="ml-2" style={{ color: '#666' }}>Scheduled</span>
                                )}
                                {(msg.status === 'queued' || msg.status === 'scheduled') && (
                                  <span className="ml-2" style={{ color: '#666' }}>Pending</span>
                                )}
                              </div>
                            )}

                            {/* Message body with clickable links */}
                            <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {msg.body?.split(/(https?:\/\/[^\s]+)/g).map((part, i) => 
                                part.match(/^https?:\/\//) ? (
                                  <a 
                                    key={i}
                                    href={part} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    style={{ color: '#0954AF' }}
                                  >
                                    {part}
                                  </a>
                                ) : part
                              )}
                            </p>

                            {/* Media preview using MediaDisplay component */}
                            {mediaUrls.length > 0 && (
                              <div className="mt-2">
                                {mediaUrls.map((url, mediaIdx) => (
                                  <MediaDisplay key={mediaIdx} url={url} index={mediaIdx} />
                                ))}
                              </div>
                            )}

                            {/* Timestamp and status */}
                            <div 
                              className="is-size-7 mt-1" 
                              style={{ 
                                textAlign: msg.direction === 'outbound' ? 'right' : 'left',
                                color: msg.status === 'failed' ? '#cc0000' : 'inherit'
                              }}
                            >
                              {formatDateTime(msg.timestamp)}
                              {msg.direction === 'outbound' && msg.status && (
                                <span className="ml-2">
                                  {msg.status === 'delivered' ? '✓✓' : 
                                   msg.status === 'sent' ? '✓' :
                                   msg.status === 'accepted' ? '✓' :
                                   msg.status === 'queued' ? '○' :
                                   msg.status === 'scheduled' ? '⏱' :
                                   msg.status === 'sending' ? '◌' :
                                   msg.status === 'pending' ? '⏱' :
                                   msg.status === 'cancelled' ? '✗ Cancelled' :
                                   msg.status === 'failed' ? '✗ Failed to send' : 
                                   msg.status === 'undelivered' ? '✗ Undelivered' : '○'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Send Error */}
                {sendError && (
                  <div className="notification is-danger is-light py-2 mb-2">
                    {sendError}
                    <button className="delete is-small" onClick={() => setSendError('')}></button>
                  </div>
                )}

                {/* Chat Input */}
                <div className="pt-3" style={{ borderTop: '1px solid #eee' }}>
                  {!canSendMessages ? (
                    <div className="notification is-warning is-light py-3">
                      <p className="is-size-7 has-text-centered">
                        <strong>Messaging Disabled</strong><br/>
                        {disabledReason}
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Attachment Preview - media only */}
                      {hasMediaAttachment && (
                        <div className="notification is-info is-light py-2 px-3 mb-2 is-flex is-align-items-center is-justify-content-space-between">
                          <span className="is-size-7 colorPureBlack">
                            {attachment.filename}
                          </span>
                          <button 
                            type="button" 
                            className="delete is-small" 
                            onClick={removeAttachment}
                          ></button>
                        </div>
                      )}

                      {/* Helper text above input */}
                      {helperText && (
                        <p className="is-size-7 has-text-grey ml-4 mb-1" style={{ fontStyle: 'italic' }}>
                          {helperText}
                        </p>
                      )}
                      
                      <form onSubmit={handleSendMessage}>
                        <div className="field has-addons mb-0">
                          <div className="control is-expanded mr-1">
                            <input
                              className="input"
                              type="text"
                              placeholder="Type a message..."
                              value={newMessage}
                              onChange={(e) => setNewMessage(e.target.value)}
                              disabled={sending || uploading}
                            />
                          </div>

                          {/* Media button - disabled when message has a link */}
                          <div className="control mx-1">
                            <label 
                              className={`button ${uploading ? 'is-loading' : ''}`} 
                              title={isMediaDisabled && messageHasLink ? 'Remove link from message to attach media' : 'Add image/video'}
                              style={isMediaDisabled ? { opacity: 0.4, pointerEvents: 'none' } : {}}
                            >
                              <span>Media</span>
                              <input 
                                type="file" 
                                accept="image/jpeg,image/png,image/gif,video/mp4"
                                onChange={handleFileSelect}
                                disabled={isMediaDisabled}
                                style={{ display: 'none' }}
                              />
                            </label>
                          </div>

                          {/* Link button - disabled when media is attached */}
                          <div className="control mx-1">
                            <button 
                              type="button" 
                              className="button" 
                              onClick={handleAddLink}
                              disabled={isLinkDisabled}
                              title={isLinkDisabled && hasMediaAttachment ? 'Remove media attachment to add a link' : 'Add link'}
                              style={isLinkDisabled ? { opacity: 0.4 } : {}}
                            >
                              Link
                            </button>
                          </div>
                          
                          {/* Send button - disabled unless text is present */}
                          <div className="control mx-1">
                            <button 
                              type="submit"
                              className={`button is-primary ${sending ? 'is-loading' : ''}`}
                              disabled={isSendDisabled}
                              style={isSendDisabled ? { opacity: 0.4 } : {}}
                            >
                              Send
                            </button>
                          </div>
                        </div>
                        <p className="is-size-7 has-text-grey mt-1 ml-4">
                          Send to {pod.members?.filter(m => m.smsVerified).length || 0} verified members
                        </p>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PodDeploymentView;